import React, { useEffect, useMemo, useState, useRef } from "react";
import { graphql } from "gatsby";
import useArticleQuery from "../../../hooks/use-article-query";
import ArticleCard from "../../cards/article";
import { Link } from "../../../../plugins/gatsby-plugin-atollon";
import Select from "react-select";
import { Search } from "../../icons";
import sal from "sal.js";

const ArticleArchive = ({ filter }) => {
  const articles = useArticleQuery();
  const [limit, setLimit] = useState(24);
  const [category, setCategory] = useState(null);
  const [searchText, setSearchText] = useState("");
  const loadRef = useRef(null);
  let timeout = useRef(null);

  const categories = [...new Set(articles.flatMap(a => a.categories.map(b => b.title)))];
  const options = [
    { value: "", label: "Filter" },
    ...categories.map(el => ({
      value: el.toLowerCase().trim().replace(/\s+/g, "-"),
      label: el
    }))
  ];

  useEffect(() => {
    sal();
  }, [category]);

  useEffect(() => {
    const onScroll = () => {
      let position = loadRef.current?.getBoundingClientRect();
      if (!position) return;
      if (position.top >= 0 && position.bottom <= window.innerHeight) {
        if (!timeout.current) {
          timeout.current = setTimeout(() => {
            setLimit(prevLimit => prevLimit + 24);
            timeout.current = null;
          }, 10);
        }
      } else {
        clearTimeout(timeout.current);
      }
    };

    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const queryParams = new URLSearchParams(window.location.search);
      const cat = queryParams.get("category");

      if (cat) {
        const formattedCat = {
          value: cat.toLowerCase().trim().replace(/\s+/g, "-"),
          label: cat,
        };
        setCategory(formattedCat);
      } else {
        setCategory(null);
      }
    }
  }, [typeof window !== "undefined" && window.location.search]);

  const updateUrl = (newCategory) => {
    const newUrl = new URL(window.location);
    if (newCategory.value) {
      newUrl.searchParams.set('category', newCategory.label);
    } else {
      newUrl.searchParams.delete('category');
    }
    window.history.replaceState(null, '', newUrl.toString());
  };

  const handleCategoryChange = (selectedCategory) => {
    setCategory(selectedCategory);
    updateUrl(selectedCategory); // Update URL immediately after state change
  };

  const handleSubmit = e => {
    e.preventDefault();
    const value = e.target.search.value;
    setSearchText(value);
  };

  const filteredArticles = useMemo(() => {
    const filteredToCategory = category?.value
      ? articles.filter(el => el.categories.some(cat => cat.title.toLowerCase().replace(/\s+/g, "-") === category.value))
      : articles;

    const searchTerms = searchText.toLowerCase().split(" ");

    return searchTerms.length > 0
      ? filteredToCategory.filter(article =>
          searchTerms.every(term => article.title.toLowerCase().includes(term))
        )
      : filteredToCategory;
  }, [searchText, category, articles]);

  return (
    <section className="article-archive">
      <div className="article-archive__inner">
        <div className="archive__filter" data-sal="slide-up" data-sal-duration="1000" data-sal-easing="ease">
          <form className="filter" onSubmit={handleSubmit}>
            <div className="filter__select">
              <Select
                openMenuOnFocus={true}
                placeholder="Filter"
                className="react-select"
                options={options}
                classNamePrefix="react-select"
                isSearchable={false}
                onChange={handleCategoryChange}
                value={category && options.find(x => x.value === category?.value)}
              />
            </div>
            <div className="filter__input">
              <input onChange={e => setSearchText(e.target.value)} type="text" id="search" name="search" placeholder="Search for something" />
              <button type="submit">
                <Search />
              </button>
            </div>
          </form>
        </div>
        <div className="article-archive__items">
          {filteredArticles.length > 0 &&
            filteredArticles.slice(0, limit).map((el, i) => (
              <Link to={el.catalogue?.url ? el.catalogue.url : el.permalink} target={el.catalogue?.url ? '_blank' : null} key={i} className="article-archive__item">
                <ArticleCard {...el} />
              </Link>
            ))}
        </div>
        <div ref={loadRef} />
      </div>
    </section>
  );
};

export default ArticleArchive;

export const query = graphql`
  fragment ArticleArchiveProps on DatoCmsArticleArchive {
    ... on DatoCmsArticleArchive {
      id
      filter
    }
  }
`;
